import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './firebase';
import Brand from './Brand';
import { Icon } from './JournalOverview';
import WorkspaceDialog from './WorkspaceDialog';
import WorkspaceArrival from './WorkspaceArrival';
import { completeLockSignOut, configureLock, isLockStorageKey, lockJournal, readLock, resetDeviceLock, unlockJournal, validPin } from './journal-lock';
import './journal-lock.css';

export function useJournalLock(uid) {
  const [state, setState] = useState(() => ({ owner: uid, ...readLock(uid) }));
  const currentUid = useRef(uid); currentUid.current = uid;
  const refresh = () => setState(previous => { const next = readLock(uid); return { owner: uid, ...next, locked: next.unavailable && previous.owner === uid ? previous.locked : next.locked }; });
  useLayoutEffect(() => { setState({ owner: uid, ...readLock(uid) }); }, [uid]);
  const active = state.owner === uid ? state : readLock(uid);
  useEffect(() => {
    if (!uid) return;
    const change = event => { if (isLockStorageKey(uid, event.key)) setState(previous => { const next=readLock(uid); return { owner:uid,...next,locked:next.unavailable&&previous.owner===uid?previous.locked:next.locked }; }); };
    window.addEventListener('storage', change);
    return () => window.removeEventListener('storage', change);
  }, [uid]);
  useLayoutEffect(() => {
    if (active.locked) document.documentElement.setAttribute('data-journal-locked', 'true');
    else document.documentElement.removeAttribute('data-journal-locked');
    return () => document.documentElement.removeAttribute('data-journal-locked');
  }, [active.locked]);
  return {
    ...active,
    lock: () => { try { lockJournal(uid); } finally { refresh(); } },
    configure: async pin => { await configureLock(uid, pin); if (currentUid.current !== uid) throw new Error('Your signed-in account changed.'); try { lockJournal(uid); } finally { refresh(); } },
    unlock: async (pin, beforeReveal) => { await unlockJournal(uid, pin, undefined, undefined, undefined, () => currentUid.current === uid); if (currentUid.current === uid) { beforeReveal?.(); refresh(); } },
    resetAfterSignOut: () => { resetDeviceLock(uid); refresh(); },
  };
}

const DeviceLockContext = createContext(null);
export const useDeviceLock = () => useContext(DeviceLockContext);
export function JournalPrivacyBoundary({ children, loading = null }) {
  const [identity, setIdentity] = useState({ ready: false, uid: null });
  const currentIdentity = useRef(identity);
  const [arrival, setArrival] = useState(null);
  const [departure, setDeparture] = useState(null);
  const arrivalSequence = useRef(0);
  useEffect(() => onAuthStateChanged(auth, user => {
    const next = { ready: true, uid: user?.uid || null };
    if (next.uid !== currentIdentity.current.uid) {
      // The auth listener confirms sign-out first; a visual never delays it.
      setDeparture(!next.uid && currentIdentity.current.uid ? `signout-${arrivalSequence.current}` : null);
      setArrival(next.uid ? { uid: next.uid, kind: 'signin', id: ++arrivalSequence.current } : null);
    }
    currentIdentity.current = next; setIdentity(next);
  }, () => setIdentity(previous => previous)), []);
  const completeArrival = useCallback(id => {
    setArrival(previous => previous?.id === id ? null : previous);
  }, []);
  const locker = useJournalLock(identity.uid);
  if (!identity.ready) return loading;
  if (identity.uid && locker.locked) {
    const uid = identity.uid;
    const signOutLockedAccount = async reset => {
      if (currentIdentity.current.uid !== uid) throw new Error('Your signed-in account changed.');
      await signOut(auth);
      if (currentIdentity.current.uid && currentIdentity.current.uid !== uid) throw new Error('Your signed-in account changed.');
      if (reset) locker.resetAfterSignOut();
      else completeLockSignOut(uid);
    };
    const unlock = async pin => {
      await locker.unlock(pin, () => {
        if (currentIdentity.current.uid === uid && !readLock(uid).locked) {
          setArrival({ uid, kind: 'unlock', id: ++arrivalSequence.current });
        }
      });
    };
    return <JournalLockScreen key={uid} signOutRequired={locker.signOutRequired} onUnlock={unlock} onSignOut={()=>signOutLockedAccount(true)} onLimitSignOut={()=>signOutLockedAccount(false)}/>;
  }
  const controller = {
    ...locker,
    lock: () => { setArrival(null); locker.lock(); },
    configure: async pin => { await locker.configure(pin); setArrival(null); },
    arrival: arrival?.uid === identity.uid ? arrival : null,
    completeArrival,
  };
  return <DeviceLockContext.Provider value={controller}>{children}{!identity.uid&&departure&&<WorkspaceArrival kind="signout" identity={departure} onDone={id=>setDeparture(previous=>previous===id?null:previous)}/>}</DeviceLockContext.Provider>;
}

export function LockSetup({ onClose, onConfigure }) {
  const [pin, setPin] = useState(''), [confirm, setConfirm] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const submit = async event => {
    event.preventDefault(); if (busy) return;
    if (!validPin(pin) || pin !== confirm) { setError('Enter the same six-digit PIN in both fields.'); return; }
    setBusy(true); setError('');
    try { await onConfigure(pin); setPin(''); setConfirm(''); onClose(); }
    catch (e) { setError(e.message || 'PIN setup failed. Check that this browser can save local settings.'); }
    finally { setBusy(false); }
  };
  return <WorkspaceDialog title="Your private pause" onClose={onClose} busy={busy} className="tl-lock-setup">
    <div className="tl-lock-setup-mark"><Icon name="lock" size={30}/></div>
    <p>Set a six-digit PIN, then lock your journal whenever you step away. No automatic timeout.</p>
    <form onSubmit={submit}>
      <label>Choose PIN<input data-autofocus aria-label="Choose device PIN" type="password" inputMode="numeric" autoComplete="off" maxLength={6} value={pin} onChange={e=>setPin(e.target.value.replace(/\D/g,''))}/></label>
      <label>Confirm PIN<input aria-label="Confirm device PIN" type="password" inputMode="numeric" autoComplete="off" maxLength={6} value={confirm} onChange={e=>setConfirm(e.target.value.replace(/\D/g,''))}/></label>
      {error&&<p className="tl-lock-error" role="alert">{error}</p>}
      <button className="tl-btn tl-btn-primary" disabled={busy||!validPin(pin)||!validPin(confirm)}><Icon name="lock" size={16}/>{busy?'Preparing your lock…':'Set PIN & lock journal'}</button>
    </form>
    <small>This PIN stays on this browser and hides the workspace from casual viewing. It does not encrypt your records or replace account sign-in. Sign out to reset a forgotten device PIN.</small>
  </WorkspaceDialog>;
}

export default function JournalLockScreen({ onUnlock, onSignOut, onLimitSignOut, signOutRequired = false }) {
  const [pin, setPin] = useState(''), [error, setError] = useState(''), [busy, setBusy] = useState(false), [hidden, setHidden] = useState(document.hidden);
  const [mustSignOut, setMustSignOut] = useState(signOutRequired);
  const input = useRef(null), screen = useRef(null), previousTitle = useRef(document.title), previousScroll = useRef({ left: window.scrollX, top: window.scrollY }), busyRef = useRef(false), mounted = useRef(true), mustSignOutRef = useRef(signOutRequired), refocusAfterFailure = useRef(false);
  useLayoutEffect(() => {
    const viewport = window.visualViewport;
    const fit = () => {
      screen.current?.style.setProperty('--tl-lock-height', `${Math.max(1, Math.round(viewport?.height || window.innerHeight))}px`);
      screen.current?.style.setProperty('--tl-lock-offset', `${Math.round(viewport?.offsetTop || 0)}px`);
    };
    fit();
    window.addEventListener('resize', fit); viewport?.addEventListener('resize', fit); viewport?.addEventListener('scroll', fit);
    return () => { window.removeEventListener('resize', fit); viewport?.removeEventListener('resize', fit); viewport?.removeEventListener('scroll', fit); };
  }, []);
  useEffect(() => {
    // Capture during the first render, before removing the taller private page can clamp its scroll position.
    const scroll = previousScroll.current;
    const root = document.getElementById('root');
    const surfaces = [
      [document.documentElement, ['overflow', 'overflow-x', 'overflow-y', 'overscroll-behavior', 'scroll-behavior']],
      [document.body, ['overflow', 'overflow-x', 'overflow-y', 'overscroll-behavior', 'position', 'top', 'left', 'right', 'width']],
      [root, ['overflow', 'overflow-x', 'overflow-y', 'overscroll-behavior']],
    ].filter(([element]) => element);
    const saved = surfaces.map(([element, properties]) => [element, properties.map(property => [property, element.style.getPropertyValue(property), element.style.getPropertyPriority(property)])]);
    for (const [element] of surfaces) {
      element.style.setProperty('overflow', 'hidden', 'important');
      element.style.setProperty('overscroll-behavior', 'none', 'important');
    }
    document.documentElement.style.setProperty('scroll-behavior', 'auto', 'important');
    for (const [property, value] of Object.entries({ position: 'fixed', top: `${-scroll.top}px`, left: `${-scroll.left}px`, right: '0', width: '100%' })) document.body.style.setProperty(property, value, 'important');
    return () => {
      for (const [element, properties] of saved) for (const [property, value, priority] of properties) {
        if (value) element.style.setProperty(property, value, priority);
        else element.style.removeProperty(property);
      }
      if (window.scrollX !== scroll.left || window.scrollY !== scroll.top) window.scrollTo({ ...scroll, behavior: 'instant' });
    };
  }, []);
  useEffect(() => {
    mounted.current = true;
    const title=previousTitle.current;
    document.title='TradeLog — Workspace locked'; input.current?.focus({preventScroll:true});
    const visibility=()=>setHidden(document.hidden); document.addEventListener('visibilitychange',visibility);
    return()=>{mounted.current=false;document.title=title;document.removeEventListener('visibilitychange',visibility);};
  },[]);
  useLayoutEffect(() => {
    if (!busy && !mustSignOut && refocusAfterFailure.current) {
      refocusAfterFailure.current = false;
      input.current?.focus({ preventScroll: true });
    }
  }, [busy, mustSignOut, error]);
  const performSignOut = useCallback(async reset => {
    setPin('');
    setError(reset ? '' : 'Signing you out to protect your workspace…');
    try {
      if (reset) await onSignOut();
      else {
        if (!onLimitSignOut) throw new Error('Automatic sign-out is unavailable.');
        await onLimitSignOut();
      }
      return true;
    } catch {
      if (mounted.current) setError(reset ? 'Sign-out failed. Check your connection and try again.' : 'Sign-out failed. Your workspace is still locked. Try signing out again.');
      return false;
    }
  }, [onSignOut, onLimitSignOut]);
  const startSignOut = useCallback(async reset => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true);
    if (!reset) { mustSignOutRef.current = true; setMustSignOut(true); }
    const signedOut = await performSignOut(reset);
    if (mounted.current && !signedOut) { busyRef.current = false; setBusy(false); }
  }, [performSignOut]);
  useEffect(() => { if (signOutRequired) startSignOut(false); }, [signOutRequired, startSignOut]);
  const verify = async value => {
    if (busyRef.current || mustSignOutRef.current || !validPin(value)) return;
    busyRef.current = true; setBusy(true); setError('');
    let signedOut = false;
    try { await onUnlock(value); if (mounted.current) setPin(''); }
    catch (e) {
      if (!mounted.current) return;
      setPin('');
      if (e.signOutRequired) {
        mustSignOutRef.current = true; setMustSignOut(true);
        signedOut = await performSignOut(false);
      } else { refocusAfterFailure.current = true; setError(e.message || 'Could not unlock this device. Try again.'); }
    } finally {
      if (mounted.current && !signedOut) {
        busyRef.current = false; setBusy(false);
      }
    }
  };
  const changePin = event => {
    if (busyRef.current || mustSignOutRef.current) return;
    const value = event.target.value.replace(/\D/g, '').slice(0, 6);
    setPin(value); setError('');
    if (validPin(value)) verify(value);
  };
  return <main ref={screen} className="tl-lock-screen" data-tl-navigation-dialog data-status={busy?'checking':error?'error':pin.length?'typing':'idle'} data-paused={hidden||undefined} data-recovery={mustSignOut||undefined}>
    <div className="tl-lock-atmosphere" aria-hidden="true"><div className="tl-lock-aura"/><div className="tl-lock-beams"><i/><i/><i/></div><div className="tl-lock-orbit tl-lock-orbit-a"/><div className="tl-lock-orbit tl-lock-orbit-b"/><div className="tl-lock-orbit tl-lock-orbit-c"/><div className="tl-lock-grid"/><svg viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice"><path d="M0 290 90 270 170 290 245 240 300 250 370 170 425 190 480 120 540 145 610 70 680 100 800 30"/><path d="M0 340 90 320 170 340 245 290 300 300 370 220 425 240 480 170 540 195 610 120 680 150 800 80"/></svg></div>
    <header><Brand size={29}/><span><i/>A PRIVATE PAUSE</span></header>
    <section className="tl-lock-content">
      <div className="tl-lock-intro">
      <div className="tl-lock-emblem" aria-hidden="true"><span className="tl-lock-emblem-outer"/><span className="tl-lock-emblem-inner"/><Icon name="lock" size={31}/></div>
      <span className="tl-eyebrow">YOUR JOURNAL. YOUR SPACE.</span>
      <h1>A moment away.<br/><em>Everything stays here.</em></h1>
      <p>Your workspace is locked. Enter your device PIN to continue. It unlocks automatically after six digits.</p>
      </div>
      <div className="tl-lock-entry">
      <form onSubmit={event=>{event.preventDefault();verify(pin);}} aria-busy={busy}>
        <label className="tl-sr-only" htmlFor="journal-unlock-pin">Device PIN</label>
        <div className="tl-lock-pin-wrap"><input ref={input} id="journal-unlock-pin" type="password" inputMode="numeric" autoComplete="off" maxLength={6} value={pin} onChange={changePin} disabled={busy||mustSignOut} aria-describedby="journal-lock-status" aria-invalid={!!error}/><div className="tl-lock-pin-dots" aria-hidden="true">{Array.from({length:6},(_,i)=><span key={i} className={i<pin.length?'is-filled':''}/>)}</div></div>
        <p id="journal-lock-status" className={error?'tl-lock-error':'tl-lock-hint'} role="status">{error||(busy?'Checking PIN…':'Six digits. Back to your flow.')}</p>
      </form>
      {mustSignOut&&<button className="tl-btn tl-btn-primary tl-lock-signout" disabled={busy} onClick={()=>startSignOut(false)}>{busy?'Signing you out…':'Try signing out again'}</button>}
      <button className="tl-lock-recovery" disabled={busy} onClick={()=>startSignOut(true)}>Forgot your PIN? Sign out & reset it</button>
      </div>
    </section>
    <footer><Icon name="shield" size={14}/><span>Device privacy screen · Five incorrect PINs will sign you out</span><span>TradeLog</span></footer>
  </main>;
}
